import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Typography, TYPOGRAPHY_WEIGHT } from '../../../components/Typography'
import { Button, BUTTON_TYPES } from '../../../components/Button'
import { BankAccountError } from '../BankAccountError/BankAccountError'
import { ConfirmPaymentPopup } from './ConfirmPaymentPopup/ConfirmPaymentPopup'
import { useFinancialsAbility } from '../Providers/FinancialsAbilityProvider'
import { getUser } from '../../../redux/store/user/getters'
import { useFinancialsContext } from '../Providers/FinancialsProvider'
import { ACTIONS, SUBJECTS } from '../Abilities'
import { BTN_TXT, TOOLTIP } from '../../../constants'
import { PaymentActionsProps } from './types'
import { FINANCIAL_STATUS, PAYMENT_TYPES } from '../types'
import { useFinancialsPopup } from '../Providers/FinancialsPopupProvider'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { getCompleteRebatesPaymentData } from './CompleteRebatesPopup/api'
import { validateCompleteRebatesPaymentData } from './CompleteRebatesPopup/helpers'

export const PaymentActions = ({
  type,
  selectedLength,
  selectedRowData,
  pageInfo,
  totalNumber,
  onResetSelection,
  updateTableData,
  selectedRebatesRowData
}: PaymentActionsProps) => {
  const {
    state: { hasBankAccount }
  } = useFinancialsContext()
  const { confirmRebatesPaymentPopup, completeRebatesPaymentPopup } =
    useFinancialsPopup()
  const user = useSelector(getUser)
  const ability = useFinancialsAbility()
  const showBankAccountError = useMemo(
    () =>
      !hasBankAccount &&
      ability.can(ACTIONS.ADD, SUBJECTS.BUSINESS_RESOURCE) &&
      (((type === PAYMENT_TYPES.REBATES || type === PAYMENT_TYPES.ADMIN_FEE) &&
        !!user.vendor) ||
        (type === PAYMENT_TYPES.MARKETPLACE_AR &&
          (!!user.vendor || !!user.community))),
    [ability, hasBankAccount, type, user.community, user.vendor]
  )
  const canInitiateRebatesPayment = useMemo(() => {
    return (
      ability.can(ACTIONS.INITIATE, SUBJECTS.REBATES_PAYMENT) &&
      type === PAYMENT_TYPES.REBATES
    )
  }, [ability, type])

  const canInitiateAdminPayment = useMemo(() => {
    return (
      ability.can(ACTIONS.INITIATE, SUBJECTS.ADMIN_PAYMENT) &&
      type === PAYMENT_TYPES.ADMIN_FEE
    )
  }, [ability, type])

  const canCompleteRebatesPayment = useMemo(() => {
    if (!selectedRebatesRowData) return false
    return (
      ability.can(ACTIONS.INITIATE, SUBJECTS.COMPLETE_REBATES_PAYMENT) &&
      type === PAYMENT_TYPES.REBATES &&
      selectedRebatesRowData.status === FINANCIAL_STATUS.PARTIALLY_PAID
    )
  }, [ability, selectedRebatesRowData, type])

  const onInitiateRebatesPayment = useCallback(() => {
    if (!selectedRebatesRowData) return
    confirmRebatesPaymentPopup.actions.open({ row: selectedRebatesRowData })
  }, [confirmRebatesPaymentPopup.actions, selectedRebatesRowData])

  const onCompleteRebatesPayment = useCallback(async () => {
    if (!selectedRebatesRowData) return
    const { data: completeRebatesPaymentData } =
      await getCompleteRebatesPaymentData(selectedRebatesRowData.uuid)
    const isValid = validateCompleteRebatesPaymentData(
      completeRebatesPaymentData
    )
    if (isValid) {
      completeRebatesPaymentPopup.actions.open({
        row: selectedRebatesRowData,
        completeRebatesPaymentData
      })
    }
  }, [completeRebatesPaymentPopup.actions, selectedRebatesRowData])

  return (
    <div className="column">
      {showBankAccountError && <BankAccountError />}
      {!!selectedLength && (
        <div className="table-wrapper__header row space-between align-center">
          <div className="column">
            <Typography.Body1 weight={TYPOGRAPHY_WEIGHT.SEMI_BOLD}>
              Selected {selectedLength} of {totalNumber}
            </Typography.Body1>
            <Typography.Caption className="page-number">
              Page {pageInfo.pageNumber} of{' '}
              {Math.ceil(totalNumber / pageInfo.pageSize)}
            </Typography.Caption>
          </div>

          {canInitiateAdminPayment && (
            <div className="table-wrapper__header_buttons">
              {selectedLength && (
                <div className="row gap-8">
                  <Button onClick={onResetSelection} upperCase>
                    {BTN_TXT.CANCEL}
                  </Button>
                  <ConfirmPaymentPopup
                    selectedRowData={selectedRowData}
                    updateTableData={updateTableData}
                    selectedLength={selectedLength}
                    onResetSelection={onResetSelection}
                    showBankAccountError={showBankAccountError}
                    type={type}
                  />
                </div>
              )}
            </div>
          )}
          {(canInitiateRebatesPayment || canCompleteRebatesPayment) && (
            <div className="table-wrapper__header_buttons">
              {selectedLength && (
                <div className="row gap-8">
                  <Button onClick={onResetSelection} upperCase>
                    {BTN_TXT.CANCEL}
                  </Button>
                  <Tooltip
                    title={
                      selectedLength > 1
                        ? TOOLTIP.PAYMENT_CANNOT_BE_INITIATED
                        : ''
                    }
                  >
                    <div>
                      {canInitiateRebatesPayment ? (
                        <Button
                          type={BUTTON_TYPES.PRIMARY}
                          onClick={onInitiateRebatesPayment}
                          upperCase
                          disabled={
                            !selectedRebatesRowData || selectedLength !== 1
                          }
                        >
                          {BTN_TXT.PAY}
                        </Button>
                      ) : (
                        <Button
                          type={BUTTON_TYPES.PRIMARY}
                          onClick={onCompleteRebatesPayment}
                          upperCase
                          disabled={
                            !selectedRebatesRowData || selectedLength !== 1
                          }
                        >
                          {BTN_TXT.COMPLETE_REBATES}
                        </Button>
                      )}
                    </div>
                  </Tooltip>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
